import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../SEO';
import { appConfig } from '../../config/appConfig';
import dentalHeroVisual from '../../images/Comp_1.gif';
import membershipVisual from '../../images/one.gif';
import operationsVisual from '../../images/dashboard.png';

const faqs = [
  {
    question: 'What dental practice valuation multiples are buyers paying right now?',
    answer:
      'Premium solo and small-group dental practices are seeing 6-10x EBITDA or 1.2-1.8x revenue depending on payer mix, specialty mix, and hygiene contribution. Strategic DSO platforms will stretch higher for high-margin, multi-location assets with clean compliance and digital systems.',
  },
  {
    question: 'How long does a DSO roll-up transaction typically take to close?',
    answer:
      'A streamlined DSO roll-up takes 5-7 months from engagement to funds-flow. We front-load diligence, convert clinical KPIs into buyer-ready dashboards, and pre-qualify PE-backed DSOs so management teams are only in critical meetings.',
  },
  {
    question: 'Do I need to stay post-close and reinvest with the buyer?',
    answer:
      'Most DSO investors want the lead doctor to remain for 18-36 months. We negotiate comp structures, equity rollover size, and post-close autonomy so you keep upside without being handcuffed beyond what is necessary for continuity.',
  },
  {
    question: 'How confidential is the AMIVA process for dental founders?',
    answer:
      'Our process keeps staff, referring dentists, and patients insulated. We anonymize collateral, stage disclosures, and gate access to data via secure rooms so only shortlisted buyers see sensitive charts once LOIs are issued.',
  },
];

const structuredData = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': 'Dental Practice M&A Advisory',
    'name': 'Dental Practice M&A Advisor (DSO Roll-Up)',
    'provider': {
      '@type': 'Organization',
      'name': appConfig.site.organizationName,
      'url': appConfig.site.baseUrl,
    },
    'areaServed': ['United States', 'Canada'],
    'audience': {
      '@type': 'Audience',
      'name': 'Dental practice owners planning a liquidity event',
    },
    'url': `${appConfig.site.baseUrl}/dentalpractice-mna-advisor`,
    'description':
      'Sell-side advisory for dental practices pursuing DSO roll-ups. We optimize valuation drivers, position financials, and orchestrate private equity-backed buyers for premium outcomes.',
    'offers': {
      '@type': 'Offer',
      'availability': 'https://schema.org/InStock',
      'price': '0',
      'priceCurrency': 'USD',
      'url': `${appConfig.site.baseUrl}/valuation`,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  },
];

const heroMetrics = [
  {
    label: 'Active Buyers',
    value: '210+',
    description: 'pre-qualified DSOs, private equity platforms, and family offices in the AMIVA grid.',
  },
  {
    label: 'Premium vs. First Offer',
    value: '32%',
    description: 'median valuation lift once calibrated KPI narratives and buyer waves run in parallel.',
  },
  {
    label: 'Competitive LOIs',
    value: '21 days',
    description: 'average time to surface 3+ qualified term sheets after data room launch.',
  },
];

const readinessChecklist = [
  'Reconcile payer mix by location and quantify fee-for-service versus PPO exposure.',
  'Normalize hygiene contribution and staffing ratios to highlight scalable margin.',
  'Lock down compliance evidence: OSHA, radiography calibrations, sterilization logs.',
  'Refresh AR aging reports, bad-debt reserves, and same-store production trending.',
  'Document associate agreements, restrictive covenants, and incentive comp plans.',
  'Audit vendor contracts (labs, supplies, marketing) for transferability and lock-in.',
  'Codify the patient experience playbook—phone scripts, recall cadence, chair utilization.',
  'Prepare pro forma scenarios reflecting growth via chair additions or daypart expansion.',
];

const highlightStats = [
  {
    label: 'DSO Outreach to LOI',
    value: '86%',
    detail: 'of clients receive term sheets from top-tier DSOs within the first buyer wave.',
  },
  {
    label: 'KPI Engagement Lift',
    value: '4.6x',
    detail: 'increase in buyer interaction time once KPI dashboards replace static PDFs.',
  },
  {
    label: 'Deal Timeline',
    value: '5-7 months',
    detail: 'concept-to-close window when readiness labs and data rooms run in tandem.',
  },
];

const listItemClasses =
  'flex items-start gap-3 text-sm sm:text-base text-gray-700 leading-relaxed';
const listDotClasses =
  'mt-1.5 h-2 w-2 flex-none rounded-full bg-amiva-primary';

const visualShowcase = [
  {
    image: membershipVisual,
    alt: 'Dental membership analytics on a tablet device.',
    label: 'Membership Mix',
    metric: '52% fee-for-service',
    caption: 'Real-time dashboards show fee-for-service lift after hygiene upgrades.',
    gradient: 'from-[#0B192E]/70 via-[#2563EB]/60 to-[#F59E0B]/60',
  },
  {
    image: dentalHeroVisual,
    alt: 'Dental leadership team reviewing a valuation playbook.',
    label: 'DSO Ready',
    metric: '6–10x EBITDA',
    caption: 'Investor collateral codifies chair utilization and EBITDA normalization.',
    gradient: 'from-[#12274A]/70 via-[#1B3A70]/60 to-[#38BDF8]/60',
  },
  {
    image: operationsVisual,
    alt: 'Operational KPI dashboard with chair utilization metrics.',
    label: 'Ops Pulse',
    metric: '21 day LOI wave',
    caption: 'Dealroom dashboards broadcast buyer activity and diligence progress.',
    gradient: 'from-[#0F172A]/70 via-[#5C6CFF]/60 to-[#F97316]/60',
  },
];

const personaProfiles = [
  {
    title: 'Multi-Location GP & Hygiene Platforms',
    stat: '3–12 locations',
    description:
      'Doctor-led groups balancing PPO and fee-for-service mix who need airtight KPI storytelling before engaging consolidators.',
  },
  {
    title: 'Specialty Pods Scaling Fast',
    stat: 'Ortho / Pedo / Perio',
    description:
      'High-margin specialty clusters seeking premium roll-up multiples while preserving specialist autonomy and referral flywheels.',
  },
  {
    title: 'Founder Succession & Partial Recap',
    stat: 'Hybrid liquidity',
    description:
      'Owners planning partial de-risking while elevating associates and hygienists into leadership narratives for the next chapter.',
  },
];

const relatedPlaybooks = [
  {
    title: 'HVAC & Home Services M&A Advisor',
    description: 'Recurring revenue storytelling for membership-driven service platforms.',
    to: '/hvac-home-services-mna-advisor',
  },
  {
    title: 'SaaS & Software M&A Advisor',
    description: 'Retention, ARR quality, and GTM efficiency packaged for software buyers.',
    to: '/saas-mna-advisor',
  },
  {
    title: 'Cross-Border M&A Advisory',
    description: 'Dual-jurisdiction buyer orchestration for founders with international demand.',
    to: '/cross-border-mna-us-europe',
  },
];

const differentiators = [
  {
    title: 'Payer Mix Intelligence',
    description:
      'Instant visuals of PPO vs. fee-for-service exposure with action steps to defend EBITDA.',
    icon: (
      <svg
        className="w-10 h-10 text-white"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18M4.5 7.5h15M4.5 16.5h15" />
        <circle cx="7" cy="7.5" r="1.5" fill="currentColor" />
        <circle cx="17" cy="16.5" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    title: 'Chair Utilization Heatmaps',
    description:
      'Timeline charts highlight capacity gaps, hygiene leverage, and implant opportunity.',
    icon: (
      <svg
        className="w-10 h-10 text-white"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h4l2 3h8l2 4H4z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 4l3 3 3-3" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 17h10l2 3H5z" />
      </svg>
    ),
  },
  {
    title: 'Clinical Compliance Stack',
    description:
      'OSHA, sterilization, and credentialing snapshots that shorten buyer diligence cycles.',
    icon: (
      <svg
        className="w-10 h-10 text-white"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M4 6h16M6 6v12a2 2 0 002 2h8a2 2 0 002-2V6" />
      </svg>
    ),
  },
];

const processHighlights = [
  {
    title: 'Readiness Sprint',
    timeline: 'Weeks 1-2',
    details: ['Data room templates', 'Clinical storytelling workshop', 'Doctor comp normalization'],
  },
  {
    title: 'Buyer Waves',
    timeline: 'Weeks 3-6',
    details: ['Tiered NDA releases', 'Narrated KPI dashboards', 'Weekly offer boards'],
  },
  {
    title: 'Deal Lock-In',
    timeline: 'Weeks 7-12',
    details: ['Term sheet sculpting', 'Post-close autonomy guardrails', 'QofE + legal orchestration'],
  },
];

export default function DentalPracticeAdvisor() {
  return (
    <div className="bg-white">
      <SEOHead
        title="Healthcare Practice M&A Advisor (DSO) | AMIVA Equity Partners"
        description="Expert sell-side advisory for dental practices. Navigate DSO roll-ups, defend valuation multiples, and close confidentially with AMIVA Equity Partners."
        keywords={[
          'dental practice M&A advisor',
          'DSO roll-up advisory',
          'dental valuation multiples',
          'sell my dental practice',
          'dental practice investment bank',
        ]}
        canonical="/dentalpractice-mna-advisor"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Industries', path: '/industries' },
          { name: 'Dental Practice M&A Advisor', path: '/dentalpractice-mna-advisor' },
        ]}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B192E] via-[#12274A] to-[#1B3A70] text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-[-6rem] -left-32 w-[28rem] h-[28rem] bg-amiva-primary/45 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-8rem] -right-20 w-[32rem] h-[32rem] bg-amiva-accent/35 blur-3xl rounded-full"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,_3fr)_minmax(0,_2.4fr)] gap-12 items-center">
            <div className="max-w-3xl">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase mb-6">
                Dental Practice M&A Advisor
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
                Translate Dental KPIs into Premium DSO Outcomes
              </h1>
              <p className="text-base sm:text-lg text-gray-200 leading-relaxed mb-6">
                We rebuild payer mix, hygiene productivity, and compliance proof so DSOs and PE
                consolidators see conviction on day one. Founders keep control of the story while
                we stage buyer waves, codify autonomy, and protect staff culture.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {heroMetrics.map((metric) => (
                  <div
                    key={metric.label}
                    className="rounded-2xl border border-white/15 bg-white/5 p-4 text-center"
                  >
                    <div className="text-2xl font-semibold text-white">{metric.value}</div>
                    <p className="mt-1 text-xs uppercase tracking-[0.28em] text-white/70">
                      {metric.label}
                    </p>
                    <p className="mt-2 text-[11px] leading-relaxed text-white/60">
                      {metric.description}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Link to="/valuation" className="btn-primary px-8 py-4 text-lg font-semibold">
                  Get a Dental Valuation
                </Link>
                <a
                  href={appConfig.links.calendly}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-base font-semibold text-white hover:text-amiva-accent transition-colors"
                >
                  Schedule a confidential conversation →
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-10 -right-6 h-28 w-28 rounded-full bg-amiva-accent/40 blur-2xl"></div>
              <div className="absolute bottom-[-3rem] -left-8 h-24 w-24 rounded-full bg-white/15 blur-xl"></div>
              <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-white/5 shadow-[0_30px_60px_rgba(11,25,46,0.45)]">
                <img
                  src={dentalHeroVisual}
                  alt="Dental valuation storyboard with KPI overlays."
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#12274A]/40 via-[#1B3A70]/10 to-transparent"></div>
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1 text-xs uppercase tracking-[0.32em] text-white">
                  Ready for DSO diligence
                </div>
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                  <p className="text-sm font-semibold text-white">
                    Hygiene uplift → +18% EBITDA normalization
                  </p>
                  <p className="mt-1 text-xs text-white/70">
                    Visual dashboards and compliance packets slot directly into the buyer QA stack.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Profiles */}
      <section className="py-16 lg:py-20 bg-gray-50" id="founder-profiles">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Founder Snapshot
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Dental founder archetypes we escort through premium DSO exits
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Whether you are running a multi-location hygiene engine or a specialty pod with
              referral gravity, the process adapts to your growth agenda, liquidity needs, and
              cultural guardrails.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {personaProfiles.map((profile) => (
              <div
                key={profile.title}
                className="rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-4 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  {profile.stat}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{profile.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{profile.description}</p>
                <div className="text-xs text-gray-500">
                  We align equity rollover, autonomy, and post-close roles before exclusivity.
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Story Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Dental Visuals
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Show the metrics buyers want without overwhelming the page
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Each industry playbook comes with visual artifacts founders can walk through in
              minutes—from payer mix lenses to dispatch dashboards—so diligence teams see the value
              instead of reading dense memos.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visualShowcase.map((shot) => (
              <div
                key={shot.label}
                className="group relative overflow-hidden rounded-3xl border border-gray-100 bg-gray-900/5 shadow-lg"
              >
                <img
                  src={shot.image}
                  alt={shot.alt}
                  className="h-60 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${shot.gradient} opacity-80 mix-blend-multiply`}
                ></div>
                <div className="absolute inset-0 flex flex-col justify-between p-6">
                  <div className="inline-flex items-center gap-2 self-start rounded-full bg-white/15 px-3 py-1 text-xs uppercase tracking-[0.28em] text-white">
                    {shot.label}
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur">
                    <div className="text-sm font-semibold text-white">{shot.metric}</div>
                    <p className="mt-1 text-xs text-white/75 leading-relaxed">{shot.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Overview Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-gradient p-8 md:p-12 rounded-3xl shadow-xl">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-10">
              <div>
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.32em] text-amiva-primary mb-2">
                  Dental M&A by the numbers
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Outcome benchmarks founders can cite with confidence
                </h2>
              </div>
              <p className="text-sm md:text-base text-gray-600 max-w-xl">
                We benchmark every process against current DSO and PE roll-up appetite so founders
                see exactly how their practice stacks up before the first NDA is signed.
              </p>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {highlightStats.map((stat) => (
              <div
                key={stat.label}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 transition-transform duration-300 hover:-translate-y-1 flex flex-col gap-3 h-full"
              >
                <div className="text-3xl sm:text-4xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amiva-primary">
                  {stat.label}
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">{stat.detail}</p>
              </div>
            ))}
          </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Why Dental Moves Differently
            </h2>
            <p className="text-base text-gray-600">
              Visual narratives replace dense clinical memos
            </p>
          </div>

          {/* Visual Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {differentiators.map((item) => (
              <div key={item.title} className="group relative overflow-hidden bg-gradient-to-br from-gray-50 to-white rounded-3xl border-2 border-gray-200 p-8 hover:border-amiva-primary transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amiva-primary/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amiva-primary to-blue-600 rounded-2xl mb-5 group-hover:scale-110 transition-transform duration-300">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl p-8 text-white">
              <div className="flex items-center gap-4">
                <div className="text-5xl font-bold">52%</div>
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider opacity-90">Hygiene Mix</div>
                  <div className="text-sm opacity-75">Revenue diversification benchmark</div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-3xl p-8 text-white">
              <div className="flex items-center gap-4">
                <div className="text-5xl font-bold">18</div>
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider opacity-90">Clinical KPIs</div>
                  <div className="text-sm opacity-75">Chair time + case acceptance tracked</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Valuation Drivers - VISUAL ICON GRID */}
      <section className="bg-gray-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What DSO Buyers Want to See
            </h2>
            <p className="text-lg text-gray-600">
              Visual dashboards replace dense memos
            </p>
          </div>
          
          {/* Visual Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-12">
            {[
              { icon: '💰', label: 'Fee-for-Service Mix', value: 'Target', sublabel: 'Higher multiples with less PPO exposure' },
              { icon: '🦷', label: 'Hygiene Production', value: 'Key Driver', sublabel: 'Recurring revenue DSOs value most' },
              { icon: '📊', label: 'Chair Utilization', value: 'Growth Signal', sublabel: 'Capacity for expansion without capex' },
              { icon: '📈', label: 'Case Acceptance', value: 'Margin Indicator', sublabel: 'Treatment plan conversion shows pricing power' },
              { icon: '✓', label: 'AS9100 Compliance', value: 'Deal Breaker', sublabel: 'OSHA, sterilization, credentialing must be current' },
              { icon: '👥', label: 'Associate Agreements', value: 'Risk Factor', sublabel: 'Restrictive covenants protect patient base' },
              { icon: '💳', label: 'AR Aging', value: 'Cash Flow', sublabel: 'Clean collections = higher working capital credit' },
              { icon: '🎯', label: 'Payer Contracts', value: 'Reimbursement', sublabel: 'PPO fee schedules impact EBITDA stability' },
              { icon: '⭐', label: 'Patient Retention', value: 'Valuation Anchor', sublabel: 'Recall system shows recurring base' },
              { icon: '🔄', label: 'Specialty Referrals', value: 'Network Effect', sublabel: 'Implant/ortho pipelines add premium' }
            ].map((metric, idx) => (
              <div key={idx} className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <div className="text-4xl mb-3 text-center">{metric.icon}</div>
                <div className="text-sm font-bold text-gray-900 mb-2 text-center">{metric.label}</div>
                <div className="text-xs font-semibold text-amiva-primary uppercase tracking-wider mb-2 text-center">{metric.value}</div>
                <div className="text-xs text-gray-600 leading-snug">{metric.sublabel}</div>
              </div>
            ))}
          </div>

          {/* Visual Process Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-8 border border-blue-200">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 rounded-2xl mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Clinical Excellence</h3>
                <p className="text-sm text-gray-700">OSHA + sterilization + imaging compliance</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-3xl p-8 border border-purple-200">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-500 rounded-2xl mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Growth Engine</h3>
                <p className="text-sm text-gray-700">Marketing ROI + referral networks + recall</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-3xl p-8 border border-green-200">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-2xl mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Team Depth</h3>
                <p className="text-sm text-gray-700">Provider retention + training + incentives</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process & Buyer Universe */}
      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase mb-4">
                  Deal choreography
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  Our Process &amp; Buyer Universe
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  We compress the sell-side story into digestible visuals—so the right DSOs, PE
                  platforms, and family offices see value instantly and act quickly.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {processHighlights.map((phase) => (
                  <div key={phase.title} className="card-premium p-6 border border-gray-100">
                    <div className="text-xs font-semibold uppercase tracking-[0.32em] text-amiva-primary mb-3">
                      {phase.timeline}
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">{phase.title}</h3>
                    <ul className="space-y-2">
                      {phase.details.map((detail) => (
                        <li key={detail} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amiva-primary"></span>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="card-gradient p-6 shadow-lg">
                <p className="text-sm text-gray-600">
                  Outcome snapshot: median 32% premium over first offer, 82% cash at close, and
                  post-close autonomy commitments codified before signing exclusivity.
                </p>
              </div>
            </div>
            <div className="card-gradient p-8 shadow-xl transition-all duration-300 hover:-translate-y-1">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Buyer Pool Highlights</h3>
              <ul className="space-y-3">
                <li className={listItemClasses}>
                  <span className={listDotClasses}></span>
                  <span>National DSOs executing specialty roll-ups and multi-site tuck-ins.</span>
                </li>
                <li className={listItemClasses}>
                  <span className={listDotClasses}></span>
                  <span>Private equity groups building new platforms for high-margin GP practices.</span>
                </li>
                <li className={listItemClasses}>
                  <span className={listDotClasses}></span>
                  <span>Family offices targeting community-rooted practices with recession resilience.</span>
                </li>
                <li className={listItemClasses}>
                  <span className={listDotClasses}></span>
                  <span>Cross-border buyers seeking U.S. footholds with advanced clinical teams.</span>
                </li>
                <li className={listItemClasses}>
                  <span className={listDotClasses}></span>
                  <span>Strategic specialists acquiring GP referral sources and implant pipelines.</span>
                </li>
              </ul>
              <p className="text-sm text-gray-500 mt-6">
                We maintain active buyer scorecards, tracking appetite, equity recycling, and
                cultural fit so you spend zero time educating unqualified acquirers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Readiness Checklist - VISUAL PROGRESS BOARD */}
      <section className="bg-gray-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Exit Readiness in 8 Moves
            </h2>
            <p className="text-base text-gray-600">
              Visual dashboard tracks every deliverable
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { num: '1', icon: '💰', title: 'Payer Mix Analysis by Location', status: 'complete' },
              { num: '2', icon: '🦷', title: 'Hygiene Contribution & Staffing Ratios', status: 'complete' },
              { num: '3', icon: '✅', title: 'OSHA, Radiography, Sterilization Logs', status: 'complete' },
              { num: '4', icon: '📊', title: 'AR Aging, Bad Debt, Production Trending', status: 'progress' },
              { num: '5', icon: '👥', title: 'Associate Agreements & Restrictive Covenants', status: 'progress' },
              { num: '6', icon: '📋', title: 'Lab, Supply, Marketing Vendor Contracts', status: 'pending' },
              { num: '7', icon: '🎯', title: 'Patient Experience & Recall Cadence', status: 'pending' },
              { num: '8', icon: '📈', title: 'Chair Addition & Daypart Expansion Pro Formas', status: 'pending' }
            ].map((item) => (
              <div
                key={item.num}
                className={`relative bg-white rounded-2xl border-2 p-6 transition-all duration-300 hover:-translate-y-1 ${
                  item.status === 'complete' ? 'border-green-300 bg-green-50/50' : 
                  item.status === 'progress' ? 'border-blue-300 bg-blue-50/50' : 
                  'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                    item.status === 'complete' ? 'bg-green-500 text-white' : 
                    item.status === 'progress' ? 'bg-blue-500 text-white' : 
                    'bg-gray-200 text-gray-600'
                  }`}>
                    {item.num}
                  </span>
                  <span className="text-3xl">{item.icon}</span>
                </div>
                <h3 className="text-xs font-semibold text-gray-900 leading-tight">{item.title}</h3>
                {item.status === 'complete' && (
                  <div className="absolute top-3 right-3">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-8 flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span className="text-gray-600">Complete</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-500"></span>
              <span className="text-gray-600">In Progress</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-gray-300"></span>
              <span className="text-gray-600">Pending</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Dental M&amp;A FAQs</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              We answer the questions that keep dental founders up at night. Explore the highlights
              below and reach out for a deeper dive tailored to your practice’s metrics.
            </p>
          </div>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="card-premium p-6 md:p-8 transition-all duration-300 hover:-translate-y-1"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-amiva-primary transition-colors">
                  {faq.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-Link */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Related Playbooks
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Explore adjacent AMIVA industry coverage
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Many founders navigate multi-vertical footprints. Dive into the playbooks below if
              your organization spans software, services, or cross-border demand alongside dental.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPlaybooks.map((item) => (
              <Link
                key={item.title}
                to={item.to}
                className="group rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1 hover:border-amiva-primary/40"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  Playbook
                </span>
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-amiva-primary">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{item.description}</p>
                <span className="text-sm font-semibold text-amiva-primary group-hover:text-amiva-accent inline-flex items-center gap-2">
                  View details
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.5 10.5L10.5 3.5M10.5 3.5H4.375M10.5 3.5V9.625"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-amiva-primary to-amiva-accent text-white py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl font-bold mb-2">Ready to Explore a DSO Roll-Up?</h2>
            <p className="text-lg text-white/90">
              Let’s benchmark your practice, structure your timeline, and orchestrate a competitive
              buyer pool that respects your legacy.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/valuation" className="bg-white text-amiva-primary font-semibold px-6 py-3 rounded-xl text-center">
              Start Your Valuation
            </Link>
            <a
              href={appConfig.links.calendly}
              target="_blank"
              rel="noreferrer"
              className="border border-white/70 rounded-xl px-6 py-3 font-semibold text-center hover:bg-white/10 transition"
            >
              Book a Strategy Call
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
